import { useMemo } from 'react';
import type { Order, OrderContractElement } from '@hero/schema';
import { CONFIG, isOrderStatus, isWaybillGenerationStale } from '@hero/core';
import { useOrderCancelOptions } from './useOrderCancel';
import {
  computeOrderDisplayInfo,
  type OrderDisplayForUser,
  type OrderDisplayPriceUi,
} from './computeOrderDisplayInfo';

const { adminNextStepConfig } = CONFIG.order;

export type UseOrderActionsConfig = {
  /** Viewer's contract leg — drives `canEditPrice` via `priceUi`. */
  viewerLeg?: OrderContractElement;
  isDriver?: boolean;
  /**
   * Legacy layout hint for non-price action surfaces. Does not gate
   * `canEditPrice` — that comes from the viewer leg.
   */
  displayForUser?: OrderDisplayForUser;
  /**
   * True when the current user/org is `Order.shipper` — enables the
   * client-first Add-price path on walk-in orders.
   */
  isViewerShipper?: boolean;
};

export interface UseOrderActionsReturn {
  isCancelable: boolean;
  canDownloadWaybill: boolean;
  isWaybillGenerating: boolean;
  canRegenerateWaybill: boolean;
  canAssignDriver: boolean;
  /** Delegates to `computeOrderDisplayInfo` → `priceUi.canEdit` (viewer leg). */
  canEditPrice: boolean;
  priceUi: OrderDisplayPriceUi;
  cancelChargesInfo?: 'yes_without_charges' | 'yes_charges_might_apply';
  cancelOptions: ReturnType<typeof useOrderCancelOptions>;
}

export function useOrderActions(
  order: Order,
  config?: UseOrderActionsConfig,
): UseOrderActionsReturn {
  const cancelOptions = useOrderCancelOptions();
  const displayForUser = config?.displayForUser ?? 'customer';

  return useMemo(() => {
    const orderStatus = order.status?.order;

    const statusConfig = orderStatus ? adminNextStepConfig[orderStatus] : undefined;
    const isCancelable = Boolean(
      statusConfig?.isCancelable === 'yes_without_charges' ||
        statusConfig?.isCancelable === 'yes_charges_might_apply',
    );
    const cancelChargesInfo = statusConfig?.isCancelable;

    const canDownloadWaybill = Boolean(order.waybill?.image?.url);
    const isWaybillGenerating =
      order.waybill?.status === 'generating' && !isWaybillGenerationStale(order);
    const hasDriverAndVehicle = Boolean(order.driver?._id && order.vehicle?._id);
    const canRegenerateWaybill =
      hasDriverAndVehicle &&
      (!isWaybillGenerating || isWaybillGenerationStale(order) || order.waybill?.status === 'failed');

    const canAssignDriver = orderStatus ? !isOrderStatus('inactive', orderStatus) : false;

    const display = computeOrderDisplayInfo({
      order,
      displayForUser,
      isDriver: config?.isDriver,
      viewerLeg: config?.viewerLeg,
      isViewerShipper: config?.isViewerShipper,
    });

    return {
      isCancelable,
      canDownloadWaybill,
      isWaybillGenerating,
      canRegenerateWaybill,
      canAssignDriver,
      canEditPrice: display.priceUi.canEdit,
      priceUi: display.priceUi,
      cancelChargesInfo,
      cancelOptions,
    };
  }, [
    order,
    displayForUser,
    config?.isDriver,
    config?.viewerLeg,
    config?.viewerLeg?.contractId,
    config?.viewerLeg?.amount,
    config?.isViewerShipper,
    order.status?.order,
    order.waybill?.image?.url,
    order.waybill?.status,
    order.doc?.updatedAt,
    order.driver?._id,
    order.vehicle?._id,
    order.contracts,
    order.client,
    order.shipper,
    cancelOptions,
  ]);
}
